import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  getTechStack,
  addToTechStack,
  updateTechStackItem,
  removeFromTechStack,
  getTechStackSummary,
  type TechStackStatus,
  type AddedFrom,
} from '@/data/techStack';
import { toast } from 'sonner';

/**
 * Hook to fetch user's tech stack
 */
export function useTechStack() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['tech-stack', user?.id],
    queryFn: () => getTechStack(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch tech stack summary
 */
export function useTechStackSummary() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['tech-stack-summary', user?.id],
    queryFn: () => getTechStackSummary(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to add product to tech stack
 */
export function useAddToTechStack() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      productId: string;
      status?: TechStackStatus;
      addedFrom?: AddedFrom;
      notes?: string;
    }) =>
      addToTechStack({
        userId: user!.id,
        ...params,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-stack'] });
      queryClient.invalidateQueries({ queryKey: ['tech-stack-summary'] });
      toast.success('Added to your tech stack');
    },
    onError: (error: any) => {
      if (error.code === '23505') {
        toast.error('This product is already in your stack');
      } else {
        toast.error('Failed to add to tech stack');
      }
    },
  });
}

/**
 * Hook to update tech stack item
 */
export function useUpdateTechStackItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      itemId: string;
      status?: TechStackStatus;
      priority?: number;
      notes?: string;
    }) => updateTechStackItem(params.itemId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-stack'] });
      queryClient.invalidateQueries({ queryKey: ['tech-stack-summary'] });
      toast.success('Tech stack updated');
    },
    onError: () => {
      toast.error('Failed to update tech stack');
    },
  });
}

/**
 * Hook to remove from tech stack
 */
export function useRemoveFromTechStack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeFromTechStack,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-stack'] });
      queryClient.invalidateQueries({ queryKey: ['tech-stack-summary'] });
      toast.success('Removed from tech stack');
    },
    onError: () => {
      toast.error('Failed to remove from tech stack');
    },
  });
}
